import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateDesProject,
  getDesProjects,
  deleteDesProjectById,
} from '../../features/projects/desprojectSlice';
import { 
  Form, 
  Input, 
  Alert, 
  Modal, 
  Checkbox, 
  Select, 
  Typography, 
  Divider,
  Button,
  Card,
  Space,
  Tag,
  Tooltip
} from 'antd';
import { 
  EditOutlined, 
  BgColorsOutlined, 
  LinkOutlined, 
  CheckCircleOutlined,
  DeleteOutlined,
  GlobalOutlined,
  CalendarOutlined,
  StarOutlined,
  StarFilled
} from '@ant-design/icons';
import MediaPreview from '../../utils/MediaPreview'; // Import the new component
import { designToolsOptions } from '../../assets/data/stack';

const { TextArea } = Input;
const { Title, Text } = Typography;
const { confirm } = Modal;

const RenderDesProj = ({ onEditContent }) => {
  const dispatch = useDispatch();
  const [success, setSuccess] = useState(false);
  const [failed, setFailed] = useState(false);
  const { desprojects } = useSelector((state) => state.desprojects);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [form] = Form.useForm();


  const groupedTools = designToolsOptions.reduce((acc, tool) => {
    if (!acc[tool.category]) {
      acc[tool.category] = [];
    }
    acc[tool.category].push(tool);
    return acc;
  }, {});

  useEffect(() => {
    dispatch(getDesProjects());
  }, [dispatch]);

  // Helper function to check if content is truly empty
  const isContentEmpty = (content) => {
    if (!content) return true;
    
    // Remove HTML tags and check if any meaningful content remains
    const textContent = content
      .replace(/<[^>]*>/g, '') // Remove all HTML tags
      .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
      .trim(); // Remove whitespace
    
    return textContent === '';
  };

  // Delete project with confirmation
  const handleDeleteId = (project) => {
    confirm({
      title: 'Delete Design Project',
      content: (
        <div>
          <p>Are you sure you want to delete <strong>"{project.name}"</strong>?</p>
          <p className="text-red-600">This action cannot be undone.</p>
        </div>
      ),
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        dispatch(deleteDesProjectById(project._id))
          .then(() => {
            dispatch(getDesProjects());
          })
          .catch(() => {
            console.log('Problem deleting project');
          });
      },
    });
  };

  // Edit project - now uses modal for basic info only
  const handleEdit = (project) => {
    setIsModalVisible(true);
    setEditingProject(project);
    
    // Set form values with project data
    form.setFieldsValue({
      name: project.name,
      desc: project.desc,
      link: project.link,
      status: project.status,
      type: project.type,
      priority: project.priority,
      stacks: project.stacks || []
    });
  };

  // Cancel the modal
  const handleCancel = () => {
    form.resetFields();
    setIsModalVisible(false);
    setEditingProject(null);
  };

  // Handle the submit event
  const handleOk = () => {
    form.submit();
  };

  const sortedProjects = [...desprojects].sort((a, b) => {
    // Sort by priority first (featured first), then by creation date
    if (a.priority === 'featured' && b.priority !== 'featured') return -1;
    if (a.priority !== 'featured' && b.priority === 'featured') return 1;
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  const handleFormSubmit = async (values) => {
    try {
      console.log('Update form values:', values);
      
      const { name, desc, link, status, type, priority, stacks } = values;

      const formData = new FormData();
      formData.append('name', name);
      formData.append('desc', desc);
      formData.append('link', link || '');
      formData.append('status', status.toString());
      formData.append('type', type);
      formData.append('priority', priority || '');
      formData.append('stacks', Array.isArray(stacks) ? stacks.join(',') : stacks);

      await dispatch(
        updateDesProject({ id: editingProject._id, formData })
      ).unwrap();
      dispatch(getDesProjects());
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
      }, 3000);

      form.resetFields();
      setIsModalVisible(false);
      setEditingProject(null);
    } catch (err) {
      console.error('Update error:', err);
      setFailed(true);
      setTimeout(() => {
        setFailed(false);
      }, 3000);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    return status ? 'green' : 'orange';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'featured':
        return 'gold';
      case 'other':
        return 'purple';
      default:
        return 'default';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'featured':
        return <StarFilled className="text-yellow-500" />;
      case 'other':
        return <StarOutlined className="text-purple-500" />;
      default:
        return null;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'ui':
        return '🎨';
      case 'ux':
        return '🧠';
      case 'combined':
        return '🎯';
      default:
        return '🎨';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'ui':
        return 'UI Design';
      case 'ux':
        return 'UX Research & Design';
      case 'combined':
        return 'UI/UX Combined';
      default:
        return 'Design';
    }
  };

  return (
    <div>
      {/* Alert Messages */}
      {success && (
        <Alert
          className="mb-6 border border-green-200 bg-green-50 rounded-lg"
          message="Design project updated successfully!"
          description="Your changes have been saved to your portfolio."
          type="success"
          showIcon
          closable
          onClose={() => setSuccess(false)}
        />
      )}
      
      {failed && (
        <Alert
          className="mb-6 border border-red-200 bg-red-50 rounded-lg"
          message="Update Failed"
          description="There was an error updating your design project. Please try again."
          type="error"
          showIcon
          closable
          onClose={() => setFailed(false)}
        />
      )}

      {/* Projects Grid */}
      {sortedProjects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedProjects.map((project) => (
            <Card
              key={project._id}
              className={`shadow-md border rounded-xl hover:shadow-lg transition-all duration-300 bg-white overflow-hidden ${
                project.priority === 'featured' 
                  ? 'border-yellow-300 bg-gradient-to-br from-yellow-50 to-white' 
                  : 'border-gray-200'
              }`}
              styles={{ body: { padding: '20px' } }}
            >
              {/* Project Media - Now using MediaPreview component */}
              <div className="mb-4 relative">
                {project.priority === 'featured' && (
                  <div className="absolute top-2 right-2 z-10">
                    <Tag color="gold" icon={<StarFilled />} className="shadow-sm">
                      Featured
                    </Tag>
                  </div>
                )}
                <MediaPreview
                  url={project.url}
                  alt={project.name}
                  className="w-full h-48"
                  placeholderIcon={BgColorsOutlined}
                  placeholderText="No Media"
                  placeholderColor="purple"
                  onClick={() => project.url && window.open(project.url, '_blank')}
                  showControls={true}
                  showBadges={true}
                  autoPlayOnHover={true}
                />
              </div>

              {/* Project Header */}
              <div className="mb-3">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">{getTypeIcon(project.type)}</span>
                    <Tag color={getStatusColor(project.status)} className="text-xs">
                      {project.status ? 'Completed' : 'In Progress'}
                    </Tag>
                    {project.priority && (
                      <Tag
                        color={getPriorityColor(project.priority)}
                        icon={getPriorityIcon(project.priority)}
                        className="text-xs"
                      >
                        {project.priority === 'featured' ? 'Featured' : 'Other'}
                      </Tag>
                    )}
                  </div>
                  <Text className="text-xs text-gray-500 flex items-center">
                    <CalendarOutlined className="mr-1" />
                    {formatDate(project.createdAt)}
                  </Text>
                </div>

                <Title level={4} className="mb-2 text-gray-800 line-clamp-1">
                  {project.name}
                </Title>

                <Text className="text-gray-600 text-sm line-clamp-2 mb-3">
                  {project.desc}
                </Text>
              </div>

              {/* Design Type */}
              <div className="mb-3">
                <Tag color="purple" className="text-xs">
                  {getTypeLabel(project.type)}
                </Tag>
              </div>

              {/* Design Tools */}
              {project.stacks && project.stacks.length > 0 && (
                <div className="mb-4">
                  <div className="flex flex-wrap gap-1">
                    {project.stacks.slice(0, 3).map((stack, index) => (
                      <Tag key={index} size="small" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                        {stack}
                      </Tag>
                    ))}
                    {project.stacks.length > 3 && (
                      <Tag size="small" className="text-xs bg-gray-50 text-gray-600">
                        +{project.stacks.length - 3} more
                      </Tag>
                    )}
                  </div>
                </div>
              )}

              {/* Project Links */}
              <div className="mb-4 flex items-center space-x-4">
                {project.link && (
                  <Tooltip title="Live Preview">
                    <Button
                      type="text"
                      size="small"
                      icon={<GlobalOutlined />}
                      onClick={() => window.open(project.link, '_blank')}
                      className="text-purple-600 hover:text-purple-800 p-1"
                    />
                  </Tooltip>
                )}
                <div className="flex-1" />
                <div className="flex items-center space-x-1">
                  <span className={`w-2 h-2 rounded-full ${
                    project.body && project.body.trim() && !isContentEmpty(project.body) ? 'bg-green-500' : 'bg-gray-300'
                  }`} />
                  <Text className="text-xs text-gray-500">
                    {project.body && project.body.trim() && !isContentEmpty(project.body) ? 'Has Case Study' : 'No Case Study'}
                  </Text>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <Space size="small">
                  <Tooltip title="Edit Case Study">
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => onEditContent && onEditContent(project)}
                      className="text-purple-600 hover:text-purple-800 hover:bg-purple-50"
                    >
                      Case Study
                    </Button>
                  </Tooltip>
                </Space>
                
                <Space size="small">
                  <Tooltip title="Edit Project">
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => handleEdit(project)}
                      className="text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                    >
                      Edit
                    </Button>
                  </Tooltip>
                  <Tooltip title="Delete Project">
                    <Button
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteId(project)}
                      className="text-red-600 hover:text-red-800 hover:bg-red-50"
                      danger
                    >
                      Delete
                    </Button>
                  </Tooltip>
                </Space>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <BgColorsOutlined className="text-6xl text-gray-400 mb-4" />
          <Title level={3} className="text-gray-500 mb-2">
            No Design Projects Yet
          </Title>
          <Text className="text-gray-400">
            Create your first design project to get started
          </Text>
        </div>
      )}

      {/* Edit Modal - Only for basic project info */}
      <Modal
        title={
          <div className="flex items-center space-x-2 pb-4 border-b border-gray-200">
            <EditOutlined className="text-purple-600" />
            <span className="text-gray-800 font-semibold">Edit Design Project</span>
          </div>
        }
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        width={800}
        centered
        className="edit-design-modal-right"
        footer={[
          <Button key="cancel" onClick={handleCancel} className="mr-2">
            Cancel
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={handleOk}
            className="bg-purple-600 hover:bg-purple-700 border-purple-600"
          >
            Update Project
          </Button>
        ]}
        destroyOnClose
        maskClosable={false}
        getContainer={false}
      >
        <div className="pt-4">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleFormSubmit}
            onFinishFailed={onFinishFailed}
            size="large"
            className="space-y-4"
          >
            {/* Basic Information */}
            <div className="mb-6">
              <Title level={5} className="text-gray-800 mb-4 flex items-center">
                <CheckCircleOutlined className="mr-2 text-purple-600" />
                Basic Information
              </Title>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Form.Item 
                  label={<span className="text-gray-700 font-medium">Project Name</span>}
                  name="name"
                  rules={[{ required: true, message: 'Please enter a project name' }]}
                >
                  <Input 
                    placeholder="Enter design project name"
                    className="h-10 border border-gray-300 rounded-lg hover:border-purple-500 focus:border-purple-500"
                  />
                </Form.Item>

                <Form.Item 
                  label={<span className="text-gray-700 font-medium">Design Type</span>}
                  name="type"
                  rules={[{ required: true, message: 'Please select a design type' }]}
                >
                  <Select 
                    placeholder="Select design type"
                    className="h-10"
                  >
                    <Select.Option value="ui">🎨 UI Design</Select.Option>
                    <Select.Option value="ux">🧠 UX Research & Design</Select.Option>
                    <Select.Option value="combined">🎯 UI/UX Combined</Select.Option>
                  </Select>
                </Form.Item>
              </div>

              <Form.Item 
                label={<span className="text-gray-700 font-medium">Project Description</span>}
                name="desc"
                rules={[{ required: true, message: 'Please enter a description' }]}
              >
                <TextArea 
                  rows={3} 
                  placeholder="Describe your design project, design process, and key design decisions..."
                  className="border border-gray-300 rounded-lg hover:border-purple-500 focus:border-purple-500 resize-none"
                />
              </Form.Item>
            </div>

            <Divider className="border-gray-200 my-4" />

            {/* Priority Section */}
            <div className="mb-6">
              <Title level={5} className="text-gray-800 mb-4 flex items-center">
                <StarOutlined className="mr-2 text-purple-600" />
                Project Priority
              </Title>

              <Form.Item
                label={
                  <span className="text-gray-700 font-medium">
                    Priority Level
                  </span>
                }
                name="priority"
                className="max-w-xs"
              >
                <Select
                  placeholder="Select priority (optional)"
                  className="h-10"
                  allowClear
                >
                  <Select.Option value="featured">⭐ Featured Project</Select.Option>
                  <Select.Option value="other">📂 Other Project</Select.Option>
                </Select>
              </Form.Item>
              
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg mt-2">
                <Text className="text-purple-700 text-xs">
                  <strong>💡 Info:</strong> Only one design project can be featured at a time.
                </Text>
              </div>
            </div>

            <Divider className="border-gray-200 my-4" />

            {/* Project Details */}
            <div className="mb-6">
              <Title level={5} className="text-gray-800 mb-4 flex items-center">
                <LinkOutlined className="mr-2 text-purple-600" />
                Project Details
              </Title>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Form.Item 
                  label={<span className="text-gray-700 font-medium">Live Preview / Prototype</span>}
                  name="link"
                >
                  <Input 
                    placeholder="https://figma.com/proto/... or live site URL"
                    prefix={<LinkOutlined className="text-gray-400" />}
                    className="h-10 border border-gray-300 rounded-lg hover:border-purple-500 focus:border-purple-500"
                  />
                </Form.Item>

                <Form.Item 
                  label={<span className="text-gray-700 font-medium">Project Status</span>}
                  name="status"
                  rules={[{ required: true, message: 'Please select project status' }]}
                >
                  <Select 
                    placeholder="Select status"
                    className="h-10"
                  >
                    <Select.Option value={false}>🚧 In Progress</Select.Option>
                    <Select.Option value={true}>✅ Completed</Select.Option>
                  </Select>
                </Form.Item>
              </div>
            </div>

            <Divider className="border-gray-200 my-4" />

            {/* Design Tools */}
            <div className="mb-4">
              <Title level={5} className="text-gray-800 mb-4 flex items-center">
                <BgColorsOutlined className="mr-2 text-purple-600" />
                Design Tools & Software
              </Title>
              
              <Form.Item 
                name="stacks"
                rules={[{ required: true, message: 'Please select at least one design tool' }]}
              >
                <Checkbox.Group className="w-full">
                  <div className="space-y-4 max-h-60 overflow-y-auto pr-2">
                    {Object.entries(groupedTools).map(([category, tools]) => (
                      <div key={category} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                        <Text className="text-gray-700 font-semibold text-xs uppercase tracking-wide mb-2 block">
                          {category}
                        </Text>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {tools.map((tool) => (
                            <Checkbox 
                              key={tool.value} 
                              value={tool.value}
                              className="text-gray-600 hover:text-gray-800 transition-colors duration-200 text-xs"
                            >
                              <span className="text-xs">{tool.label}</span>
                            </Checkbox>
                          ))}
                        </div>
                      </div>
                    ))}
                    
                    {/* Design Tips Section */}
                    <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                      <Text className="text-gray-600 text-xs">
                        💡 <strong>Pro Tip:</strong> Select the primary tools you used for this project to help showcase your design capabilities.
                      </Text>
                    </div>
                  </div>
                </Checkbox.Group>
              </Form.Item>
            </div>
          </Form>
        </div>
      </Modal>
    </div>
  );
};

export default RenderDesProj;